/**
 * Environment configuration for ProductTrace frontend
 */

interface EnvironmentConfig {
  apiBaseUrl: string;
  environment: 'development' | 'staging' | 'production';
  appName: string;
  version: string;
}

// Get environment variables with fallbacks
const getEnvVar = (key: string, defaultValue: string): string => {
  // @ts-ignore - Vite environment variables
  return import.meta.env[key] || defaultValue;
};

// Dynamically determine API base URL based on current location
const getApiBaseUrl = (): string => {
  // In development, use the environment variable or localhost
  if (getEnvVar('VITE_APP_ENV', 'development') === 'development') {
    return getEnvVar('VITE_API_BASE_URL', 'http://localhost:8000');
  }
  
  // In production, use the same protocol and host as the current page
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol; // 'http:' or 'https:'
    const host = window.location.host; // 'producttrace.io' or 'localhost:3000'
    return `${protocol}//${host}`;
  }
  
  // Fallback for SSR or when window is not available
  return getEnvVar('VITE_API_BASE_URL', 'https://producttrace.io');
};

export const config: EnvironmentConfig = {
  apiBaseUrl: getApiBaseUrl(),
  environment: getEnvVar('VITE_APP_ENV', 'development') as 'development' | 'staging' | 'production',
  appName: getEnvVar('VITE_APP_NAME', 'ProductTrace'),
  version: getEnvVar('VITE_APP_VERSION', '1.0.0'),
};

// API endpoints
export const API_ENDPOINTS = {
  // Products
  products: `${config.apiBaseUrl}/api/products`,
  productBySlug: (slug: string) => `${config.apiBaseUrl}/api/products/slug/${slug}`,
  productAnalyze: `${config.apiBaseUrl}/api/products/analyze`,
  productAnalyzeConfirm: `${config.apiBaseUrl}/api/products/analyze/confirm`,
  productRefreshAnalysis: (id: string) => `${config.apiBaseUrl}/api/products/${id}/refresh-analysis`,
  
  // Components
  components: `${config.apiBaseUrl}/api/components`,
  componentBySlug: (slug: string) => `${config.apiBaseUrl}/api/components/slug/${slug}`,
  
  // Jobs
  jobs: `${config.apiBaseUrl}/api/jobs`,
  
  // Health
  health: `${config.apiBaseUrl}/health`,
  
  // Admin
  admin: {
    stats: `${config.apiBaseUrl}/api/admin/stats`,
    logs: `${config.apiBaseUrl}/api/admin/logs`,
    versions: (entityType: string, entityId: string) => 
      `${config.apiBaseUrl}/api/admin/versions/${entityType}/${entityId}`,
    revertLog: (logId: string) => `${config.apiBaseUrl}/api/admin/logs/${logId}/revert`,
    updateProduct: (productId: string) => `${config.apiBaseUrl}/api/admin/products/${productId}`,
    updateComponent: (componentId: string) => `${config.apiBaseUrl}/api/admin/components/${componentId}`,
    updateProductData: (productId: string) => `${config.apiBaseUrl}/api/admin/products/${productId}/data`,
    updateComponentData: (componentId: string) => `${config.apiBaseUrl}/api/admin/components/${componentId}/data`,
    revertProduct: (productId: string) => `${config.apiBaseUrl}/api/admin/products/${productId}/revert`,
    revertComponent: (componentId: string) => `${config.apiBaseUrl}/api/admin/components/${componentId}/revert`,
  },
  public: {
    stats: `${config.apiBaseUrl}/api/products/stats/public`,
  },
};

export default config; 